import React, { useState, useEffect } from "react";
import toastr from "toastr";
import { Helmet } from "react-helmet";
import { authFetch } from "../utils/authFetch"; 
import { hasPerm } from "../utils/permission";

/* ───────── helpers ───────── */
const toast = (msg, type = "success") => {
  toastr.options = { closeButton: true, progressBar: true, timeOut: "3000" };
  toastr[type](msg);
};

// const baseURL = "http://127.0.0.1:6060/api/v1/apitoken";
// const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };

/* ───────────────── component ───────────────── */
function ApiToken() {
  const emptyForm = {
    ip: "",
    domain: "",
    callbackurl: "",
    redirecturl: "",
    // companycode: "",
  };

  const [form, setForm] = useState(emptyForm);
  const [tokens, setTokens] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const roleId = currentUser.role_id;

  /* ─── fetch list ─── */
  const fetchTokens = async () => {
    try {
      // const res = await fetch(`${baseURL}/list`, { headers: authHeader });
      const res = await authFetch("/api/v1/apitoken/list");
      if (!res) return;

      const data = await res.json();
      const normal = (Array.isArray(data) ? data : []).map((t) => ({
        ...t,
        id: t.id || t._id, // ensure .id
      }));
      setTokens(normal);
    } catch {
      toast("Failed to fetch tokens", "error");
    }
  };

  useEffect(() => { fetchTokens(); }, []);

  /* ─── create / update ─── */
  const saveToken = async () => {
    if (!form.ip || !form.domain) {
      return toast("IP & Domain are required", "error");
    }
    setLoading(true);

    // const url = editingId ? `${baseURL}/update/${editingId}` : `${baseURL}/create`;
    const url = editingId ? `/api/v1/apitoken/update/${editingId}` : "/api/v1/apitoken/create";
    const method = editingId ? "PUT" : "POST";

    try {
      const res = await authFetch(url, {
        method,
        // headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Request failed");

      toast(editingId ? "Token updated" : "Token created", editingId ? "info" : "success");
      setForm(emptyForm);
      setEditingId(null);
      fetchTokens();
      document.querySelector("#tokenModal .btn-close")?.click();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  /* ─── edit ─── */
  const handleEdit = (t) => {
    setForm({
      ip: t.ip ?? "",
      domain: t.domain ?? "",
      callbackurl: t.callbackurl ?? "",
      redirecturl: t.redirecturl ?? "",
      // companycode: t.companycode ?? "",
    });
    setEditingId(t.id);
  };

  /* ─── delete ─── */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this token?")) return;
    try {
      // const res = await fetch(`${baseURL}/delete/${id}`, { method: "DELETE", headers: authHeader });
      const res = await authFetch(`/api/v1/apitoken/delete/${id}`, { method: "DELETE" });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Delete failed");
      toast("Token deleted", "error");
      fetchTokens();
    } catch (err) {
      toast(err.message, "error");
    }
  };

  /* ─── input change ─── */
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  /* ─── render ─── */
  return (
    <>
    <Helmet>
      <title>API Token | SuvidhaPe</title>
    </Helmet>
    <div className="page-wrapper">
      <div className="page-content-wrapper">
        <div className="page-content">
          <div className="card">
            <div className="card-body">
              {/* header */}
              <div className="card-title d-flex justify-content-between align-items-center">
                <h4 className="mb-0">API Token Manager</h4>
                {hasPerm("api_token_add") && roleId !== "1" && (
                <button
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#tokenModal"
                  onClick={() => { setForm(emptyForm); setEditingId(null); }}
                >
                  Add
                </button>
                )}
              </div>

              {/* modal */}
              <div className="modal fade" id="tokenModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog"><div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{editingId ? "Edit Token" : "Add Token"}</h5>
                    <button type="button" className="btn-close" data-bs-dismiss="modal" />
                  </div>
                  <div className="modal-body">
                    {["ip", "domain", "callbackurl", "redirecturl"].map((f) => (
                      <div key={f}>
                        <label className="form-label text-capitalize">{f}</label>
                        <input
                          name={f}
                          className="form-control mb-2"
                          value={form[f]}
                          onChange={onChange}
                          // disabled={!!editingId && (f === "ip" || f === "domain")}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                    <button className="btn btn-primary" onClick={saveToken} disabled={loading}>
                      {loading ? "Saving…" : editingId ? "Update" : "Save"}
                    </button>
                  </div>
                </div></div>
              </div>

              {/* table */}
              <hr />
              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th>IP</th>
                      <th>Token</th>
                      <th>Name</th>
                      <th>Domain</th>
                      <th>Callback URL</th>
                      <th>Redirect URL</th>
                      {/* <th>Company Code</th> */}
                      {hasPerm("api_token_edit") && roleId !== "1" && (
                      <th>Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {tokens.length === 0 && (
                      <tr><td colSpan="6" className="text-center">No Tokens found</td></tr>
                    )}
                    {tokens.map((t) => (
                      <tr key={t.id}>
                        <td>{t.ip}</td>
                        <td>{t.token}</td>
                        <td>{t.user_name}</td>
                        <td>{t.domain}</td>
                        <td>{t.callbackurl}</td>
                        <td>{t.redirecturl}</td>
                        {/* <td>{t.companycode}</td> */}
                        {hasPerm("api_token_edit") && roleId !== "1" && (
                        <td>
                          <button className="btn btn-sm btn-info me-2" data-bs-toggle="modal"
                            data-bs-target="#tokenModal" onClick={() => handleEdit(t)}>
                            <span className="bx bx-edit" />
                          </button>
                          {/* <button className="btn btn-sm btn-danger" onClick={() => handleDelete(t.id)}>
                            <span className="bx bx-trash" />
                          </button> */}
                        </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default ApiToken;
